import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  reasonSchema,
  type ReasonFormInput,
  type ReasonFormOutput,
} from '../schemas/auction-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['reason']);

export interface ReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  label: string;
  hint?: string;
  confirmLabel: string;
  pending: boolean;
  destructive?: boolean;
  /** Resolves when done; a rejection is shown in the dialog. */
  onConfirm: (reason: string) => Promise<unknown>;
}

/** Cancel, remove a bid, block a bidder: each asks why, and the backend audits the answer. */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  hint,
  confirmLabel,
  pending,
  destructive = true,
  onConfirm,
}: ReasonDialogProps) {
  const { t } = useTranslation(['auctions', 'common']);
  const message = useErrorMessage();
  const form = useForm<ReasonFormInput, unknown, ReasonFormOutput>({
    resolver: useI18nResolver(reasonSchema),
    defaultValues: { reason: '' },
  });

  const close = () => {
    onOpenChange(false);
    form.reset({ reason: '' });
  };

  const submit = form.handleSubmit(async ({ reason }) => {
    try {
      await onConfirm(reason);
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{label}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} autoFocus {...field} />
                  </FormControl>
                  {hint ? <FormDescription>{hint}</FormDescription> : null}
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                type="submit"
                variant={destructive ? 'destructive' : 'default'}
                disabled={pending}
              >
                {pending ? t('auctions:actions.working') : confirmLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
